"use client";

interface GradientCircleProps {
    value: string;
    title?: string;
    description?: string;
    color?: string;
    className?: string;
    hasGradient?: boolean;
    gradientFrom?: string;
    gradientTo?: string;
    hasGrainyEffect?: boolean;
    showArrow?: boolean;
}

export function GradientCircle({
    value,
    title,
    description,
    color = "bg-ss-light-green",
    className = "",
    hasGradient = false,
    gradientFrom,
    gradientTo,
    hasGrainyEffect = false,
    showArrow = false
}: GradientCircleProps) {

    // Simple color class generation
    const getColorClass = (colorName: string) => {
        // If it already starts with 'bg-ss-', use it as is
        if (colorName.startsWith('bg-ss-')) {
            return colorName;
        }
        // Otherwise, prepend 'bg-ss-'
        return `bg-ss-${colorName}`;
    };

    const colorClass = getColorClass(color);

    // Check if value is just an integer or percentage
    const is_numeric_value = /^(\d+|\d+\.\d+)%?$/.test(value.trim());
    const value__text_size = is_numeric_value 
        ? 'text-lg md:text-2xl lg:text-3xl'
        : 'text-xs sm:text-sm md:text-base lg:text-lg';

    return (
        <div className={`flex flex-col justify-between border w-full md:w-fit mx-auto py-5 md:py-0 rounded-2xl md:rounded-none md:border-none items-center h-full ${className}`}>
            {/* Title at the top */}
            {title && (
                <div className="text-center z-20 relative mb-4 w-full">
                    <h4 className={`font-serif font-semibold leading-tight`}>{title}</h4>
                </div>
            )}
            
            {/* Circle in the middle */}
            <div className="relative shrink-0">
                {/* Circle with gradient and texture */}
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-40 lg:h-40 mx-auto">
                    {/* White circle with arrow - positioned below on mobile, top left on desktop */}
                    {showArrow && (
                        <div className={`w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-white rounded-full flex items-center justify-center shadow-lg z-30 absolute ${
                            // Mobile: Top left of circle on all screen sizes
                            '-top-1 -left-1'
                        }`}>
                            <svg 
                                width="24" 
                                height="24"
                                className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-ss-green" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                            >
                                <path 
                                    d="M4 12L20 12M20 12L14 6M20 12L14 18" 
                                    stroke="currentColor" 
                                    strokeWidth="2.5" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    )}
                    
                    <div 
                        className={`w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-36 lg:h-36 rounded-full flex items-center justify-center font-bold overflow-hidden absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 ${
                            hasGradient && !gradientFrom && !gradientTo ? '' : colorClass
                        }`}
                        style={{
                            background: hasGradient && gradientFrom && gradientTo 
                                ? `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`
                                : undefined,
                        }}
                    >
                        {/* Grainy texture overlay */}
                        {hasGrainyEffect && (
                            <div 
                                className="absolute inset-0 opacity-40 pointer-events-none rounded-full" 
                                style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='6' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                                    backgroundSize: '150px 150px'
                                }}
                            />
                        )}
                        <span className={`relative z-10 text-center leading-tight px-1 text-white ${value__text_size}`}>
                            {value}
                        </span>
                    </div>
                </div>
            </div>
            
            {description && (
                <div className="text-center z-20 relative mt-4 w-full">
                    <p className={`text-xs leading-tight max-w-[300px] sm:max-w-[240px] md:max-w-[280px] lg:max-w-[320px] mx-auto`}>{description}</p>
                </div>
            )}
        </div>
    );
}
